import { createContext, useContext, useState, useCallback } from 'react'

const ToastContext = createContext(null)

let toastId = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, duration)
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const toast = {
    success: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'error'),
    info: (msg) => addToast(msg, 'info'),
    warning: (msg) => addToast(msg, 'warning'),
  }

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within ToastProvider')
  return context
}

// Toast container rendered at root level
function ToastContainer({ toasts, onRemove }) {
  if (!toasts.length) return null

  return (
    <div style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  )
}

const TOAST_STYLES = {
  success: {
    background: '#F0FDF4',
    border: '1px solid #BBF7D0',
    color: '#15803D',
    icon: '✓',
  },
  error: {
    background: '#FFF1F2',
    border: '1px solid #FECDD3',
    color: '#BE123C',
    icon: '✕',
  },
  warning: {
    background: '#FFF7ED',
    border: '1px solid #FED7AA',
    color: '#C2410C',
    icon: '⚠',
  },
  info: {
    background: '#EFF6FF',
    border: '1px solid #BFDBFE',
    color: '#1D4ED8',
    icon: 'i',
  },
}

function ToastItem({ toast, onRemove }) {
  const styles = TOAST_STYLES[toast.type] || TOAST_STYLES.info

  return (
    <div
      className="animate-slide-right"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 20px',
        borderRadius: 10,
        background: styles.background,
        border: styles.border,
        color: styles.color,
        fontSize: 13,
        fontWeight: 500,
        boxShadow: 'var(--shadow-md)',
        minWidth: 280,
        maxWidth: 400,
        cursor: 'pointer',
      }}
      onClick={() => onRemove(toast.id)}
    >
      <span style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: styles.color,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}>
        {styles.icon}
      </span>
      <span style={{ flex: 1 }}>{toast.message}</span>
    </div>
  )
}